// An agent's recent runs (agents.runs, gap #18): what each was asked, how it ended, its steps and,
// for the owner only (agents.manage), its cost; a flag says it the way the approvals card does.

import { UNTRUSTED_FLAG } from '../shared/ApprovalsInbox.tsx';

/** agents.runs' row, as the screen reads it. */
export interface RunSummary {
  id: string;
  agentKey: string;
  trigger: string;
  task: string;
  status: string;
  stopReason: string | null;
  steps: number;
  /** The owner's only (agents.manage); absent for anyone else (QA-542). */
  costCents?: number;
  flags: string[];
  answer: string | null;
  startedAt: string;
  endedAt: string | null;
  hidden: string | null;
}

/** How a run ended, in words, and its chip's tone. */
export const RUN_STATUS: Record<string, [string, string]> = {
  running: ['Running', ''],
  done: ['Done', 'ok'],
  stopped: ['Stopped', 'warn'],
  failed: ['Failed', 'bad'],
};
export const runStatus = (s: string): [string, string] => RUN_STATUS[s] ?? [s, ''];

/** A run's flags in words: the approvals card's for text that read like instructions. */
const FLAG_WORDS: Record<string, string> = {
  [UNTRUSTED_FLAG]: 'Instructions found in customer text',
};
export const flagWords = (flags: readonly string[]) =>
  flags.map((f) => FLAG_WORDS[f] ?? f.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()));

/** When a run started: the time today, else "Oct 15, 9:19 a.m." (the list goes back days). */
export function startedText(iso: string, timeZone: string, now = new Date()): string {
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'short' }).format(d);
  const time = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' })
    .format(new Date(iso))
    .replace('AM', 'a.m.')
    .replace('PM', 'p.m.');
  if (day(new Date(iso)) === day(now)) return time;
  const date = new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric' }).format(
    new Date(iso),
  );
  return `${date}, ${time}`;
}

/** The model's cost, to a hundredth of a cent ("$0.0123"), for the owner only. */
export const costText = (cents: number) => `$${(cents / 100).toFixed(4)}`;

/** What started a run, in words (agent_runs.trigger). */
const TRIGGER_WORDS: Record<string, string> = {
  on_demand: 'asked by a person',
  schedule: 'on its schedule',
  event: 'after something happened',
  call: 'on a call',
};
export const triggerWords = (t: string) => TRIGGER_WORDS[t] ?? t.replace(/_/g, ' ');

/** "6 steps" or "1 step, stopped: the budget is spent" (the chip says how it ended). */
export function runLine(r: Pick<RunSummary, 'steps' | 'stopReason'>): string {
  return `${r.steps} ${r.steps === 1 ? 'step' : 'steps'}${r.stopReason ? `, stopped: ${r.stopReason}` : ''}`;
}
